/*
 * THE DRAWING PRIMITIVES — the three shapes every layer of the map is made of.
 *
 * Ported from `reference/universe-v8/engine.js` — `outerR` (741–746), `neb` (1295–1300) and
 * `strand` (1303–1322). Checked against those by running them against a recording context.
 *
 * GRADIENTS ARE BACK, AND THAT IS THE POINT. The first port's `DrawContext` deliberately exposed
 * neither `createLinearGradient` nor `createRadialGradient`, and every design feature needing one
 * was re-expressed as batched flat geometry — recorded as deviation 4 in `UNIVERSE-MAP.md`, with a
 * real reason: a gradient per edge means a stroke per edge, which was the flush regression
 * `ARCHITECTURE.md` §Phase 3 removed.
 *
 * That reason was true of OUR renderer and is false of this one. Theirs affords a gradient per
 * strand because it draws so few strands: the level of detail, the fan cap and the tile window
 * between them decide that only a handful are worth a flush at all. Re-applying the old workaround
 * here would be the knockoff arriving by the back door — the same instinct that produced a
 * segmented taper standing in for a gradient, and a map that looked nearly right.
 */

/** A gradient being built. Both kinds are used, and neither is ever held across a frame. */
export interface DrawGradient {
  addColorStop(offset: number, colour: string): void;
}

/**
 * The 2D surface this renderer draws through.
 *
 * Wider than the first port's, because it has to be: a real canvas, an OffscreenCanvas in a worker,
 * and the recording stub the differential tests use all satisfy it, and it is honest about what
 * their engine actually asks for.
 */
export interface DrawContext {
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  scale(x: number, y: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  /** A true ellipse path. The rungs are circles in the world and ovals once the world is tilted. */
  ellipse(x: number, y: number, rx: number, ry: number, rotation: number, start: number, end: number): void;
  /** The dash pattern for subsequent strokes. An empty array is a solid line. */
  setLineDash(pattern: readonly number[]): void;
  /**
   * How far along the pattern a dashed stroke starts.
   *
   * The one property in the engine that exists to be ANIMATED rather than set: the trail marches
   * because this moves, not because the line does. Nothing else here reads it.
   */
  lineDashOffset: number;
  rect(x: number, y: number, w: number, h: number): void;
  /**
   * A rounded rectangle, where the host canvas has one.
   *
   * Optional because theirs tests for it and falls back to `rect` — a square corner is a worse
   * shape, not a broken one, and the fallback is the mechanism rather than a nicety.
   */
  roundRect?(x: number, y: number, w: number, h: number, radii: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  /** Replace the whole matrix. How a frame resets to device pixels before it draws anything. */
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  /**
   * Blit an image. The source is whatever the host canvas accepts — an `HTMLCanvasElement`, an
   * `OffscreenCanvas` — so it is opaque here rather than tied to either.
   */
  /**
   * Blit an image. The source is whatever the host canvas accepts — an `HTMLCanvasElement`, an
   * `OffscreenCanvas` — so it is opaque here rather than tied to either.
   *
   * The width and height are optional because the canvas API has both forms and the engine uses
   * both: the wash gives a destination rectangle in world units, and the static blit gives none
   * at all because it is copying one image onto another of the same size.
   */
  drawImage(image: unknown, x: number, y: number, w?: number, h?: number): void;
  closePath(): void;
  fill(): void;
  stroke(): void;
  fillText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): DrawGradient;
  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number): DrawGradient;
  fillStyle: string | DrawGradient;
  strokeStyle: string | DrawGradient;
  lineWidth: number;
  globalAlpha: number;
  font: string;
  textAlign: string;
  /** Where the baseline sits. `'middle'` is what centres a wedge label on its own point. */
  textBaseline: string;
  /** Round while strands are drawn, so a trimmed end reads as a taper rather than a cut. */
  lineCap: string;
}

/** Their turn, in radians. Written out as 6.2832 in the reference; the same number, named. */
const TAU = 6.2832;

/**
 * A NEBULA: an ellipse of radial gradient, rotated.
 *
 * Drawn as a scaled circle rather than as an ellipse path, because a radial gradient is round and
 * scaling the space is the only way to make it oval. The save/restore is what keeps that scale from
 * leaking into everything drawn after it.
 */
export function neb(
  ctx: DrawContext,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  rot: number,
  stops: readonly (readonly [number, string])[],
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot || 0);
  ctx.scale(1, ry / rx);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
  for (const [at, colour] of stops) g.addColorStop(at, colour);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, rx, 0, TAU);
  ctx.fill();
  ctx.restore();
}

/**
 * HOW FAR A FIGURE'S MARK REACHES beyond its own disc.
 *
 * The corpus ring sits outside the disc, and the presence ring outside that, so anything that has
 * to clear a figure — a strand's gap, a label's box — has to clear the widest of them. Their floor
 * of `r + 11` is what stops a two-pixel speck being touched by everything near it.
 *
 * `corpora` is the count of texts naming this figure and `presence` how many readers are standing
 * on it: both DATA, and both passed rather than read off a node, so this stays arithmetic.
 */
export function outerR(r: number, corpora: number, presence: number): number {
  const ring = corpora > 1 && r >= 3.2
    ? r + Math.max(2.6, r * 0.42) + Math.max(1.2, Math.min(3.2, r * 0.2)) * 0.5
    : r;
  const pres = presence ? r + Math.max(5.5, r * 0.78) + 2 : r;
  return Math.max(ring, pres, r + 11);
}

/** The colours a strand gathers through. Theirs, and a look decision rather than a measurement. */
const STRAND_HEAD = '244,238,222';
const STRAND_BODY = '228,221,203';
const STRAND_TAIL = '208,203,188';

/**
 * ONE STRAND RENDERER, shared by the selected figure's fan and the ambient web.
 *
 * Their comment: *"trimmed clear of both discs, drawn as a gradient that gathers from transparent
 * and falls away again."* The trimming is what makes the web read as connections between things
 * rather than as lines through them, and the gradient is what stops ten thousand of them reading as
 * a grey wash: each one is brightest a sixth of the way along and gone at both ends.
 *
 * Returns whether anything was drawn. Two refusals, and both matter:
 *
 * - a strand shorter than its two gaps plus fourteen pixels has nothing left to draw once trimmed;
 * - a strand whose visible span is under eight per cent of its length is a smudge, not a line.
 *
 * `far` is the rumour jump — a tie to somewhere off the far side of the map. It is drawn only as a
 * leading stub (42% of the way) rather than all the way across, because the whole line would be a
 * rule across the screen pointing at nothing the reader can see.
 */
export function strand(
  ctx: DrawContext,
  ax: number, ay: number,
  cx: number, cy: number,
  bx: number, by: number,
  gapA: number, gapB: number,
  head: number,
  width: number,
  far: boolean,
): boolean {
  const len = Math.hypot(bx - ax, by - ay);
  if (len < gapA + gapB + 14) return false;
  const u0 = gapA / len;
  const u1 = far ? Math.min(0.95, u0 + 0.42) : 1 - gapB / len;
  if (u1 - u0 < 0.08) return false;

  /* A point along the quadratic, so the trimmed ends sit on the curve rather than on the chord. */
  const qp = (u: number, p: number, c: number, q: number): number => {
    const k = 1 - u;
    return k * k * p + 2 * k * u * c + u * u * q;
  };
  const p0x = qp(u0, ax, cx, bx);
  const p0y = qp(u0, ay, cy, by);
  const p1x = qp(u1, ax, cx, bx);
  const p1y = qp(u1, ay, cy, by);
  /* The control point that makes the TRIMMED span follow the original curve: the midpoint of the
     full curve, doubled and pulled back off the new chord. */
  const cmx = qp((u0 + u1) / 2, ax, cx, bx) * 2 - (p0x + p1x) / 2;
  const cmy = qp((u0 + u1) / 2, ay, cy, by) * 2 - (p0y + p1y) / 2;

  const g = ctx.createLinearGradient(p0x, p0y, p1x, p1y);
  g.addColorStop(0, `rgba(${STRAND_HEAD},0)`);
  g.addColorStop(0.16, `rgba(${STRAND_HEAD},${head.toFixed(3)})`);
  g.addColorStop(far ? 0.5 : 0.62, `rgba(${STRAND_BODY},${(head * 0.42).toFixed(3)})`);
  g.addColorStop(1, `rgba(${STRAND_TAIL},0)`);
  ctx.strokeStyle = g;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(p0x, p0y);
  ctx.quadraticCurveTo(cmx, cmy, p1x, p1y);
  ctx.stroke();
  return true;
}
